import { isEqual } from 'lodash-es';
import { getLocalTimeZone, parseZonedDateTime } from '@internationalized/date';
import { ModifiedFieldValue } from '@lp/features/RoutePlanner/components/LogisticsChangeLog/utils';
import { formatDate, getTzDateTimeString } from '@/utils/helpers';
import {
  EventFieldLog,
  EventOperationType,
  ModifiedEventLog,
  ModifiedEventLogType,
  ModifiedLogLegType,
} from '@/api/apis/route';

export const getChangeLogDateTimeString = (
  timestamp: string,
  formatString: string = 'MMM d yyyy HH:mm zzz',
  timezone: string = getLocalTimeZone(),
) => {
  const dateTimeString = getTzDateTimeString(timestamp);
  const utcString = parseZonedDateTime(dateTimeString).toAbsoluteString();

  return formatDate(new Date(utcString), formatString, { timezone });
};

export const isEventFieldType = <T = unknown>(value: unknown): value is EventFieldLog<T> => {
  return (
    value !== null &&
    typeof value === 'object' &&
    'operationType' in value &&
    'previousValue' in value &&
    'newValue' in value
  );
};

export const isPrimitive = (
  value: unknown,
): value is string | number | boolean | bigint | symbol | null | undefined => {
  return value === null || (typeof value !== 'object' && typeof value !== 'function');
};

export const getPreviousEventValues = <T>(eventField: EventFieldLog<T>): T | null => {
  switch (eventField.operationType) {
    case EventOperationType.Removed:
    case EventOperationType.Modified:
    case EventOperationType.NoChange:
      return eventField.previousValue;
    case EventOperationType.Added:
    default:
      return null;
  }
};

export const getCurrentEventValues = <T>(eventField: EventFieldLog<T>): T | null => {
  switch (eventField.operationType) {
    case EventOperationType.Added:
    case EventOperationType.Modified:
      return eventField.newValue;
    case EventOperationType.NoChange:
      return eventField.previousValue;
    case EventOperationType.Removed:
    default:
      return null;
  }
};

export const unwrapModifiedEventLog = <T, R extends PropertyKey = never>(
  data: ModifiedEventLog<T, R>,
  getEventFieldValue: (eventField: EventFieldLog<unknown>) => unknown | null = getCurrentEventValues,
): T => {
  const unwrapValue = (value: unknown): unknown => {
    // Stop if primitive
    if (isPrimitive(value)) {
      return value;
    }

    // Handle arrays (unwrap items)
    if (Array.isArray(value)) {
      return value.map(unwrapValue);
    }

    // Handle EventFieldType<T>
    if (isEventFieldType(value)) {
      return getEventFieldValue(value);
    }

    // Handle objects (unwrap fields recursively)
    if (value !== null && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      for (const key of Object.keys(value)) {
        const val = (value as Record<string, unknown>)[key];
        result[key] = unwrapValue(val);
      }
      return result;
    }

    // Fallback
    return value;
  };

  return unwrapValue(data) as T;
};

export const prepareChangedValues = <T, F extends keyof T = never>(
  leftValue: Record<F, T[F]>,
  rightValue: Record<F, T[F]>,
): Record<F, T[F]> => {
  return Object.keys(leftValue).reduce(
    (acc, fieldName) => {
      const isOldValueArray = Array.isArray(leftValue[fieldName as F]);
      const isNewValueArray = Array.isArray(rightValue[fieldName as F]);

      if (isOldValueArray && isNewValueArray) {
        const leftArray = leftValue[fieldName as F] as Array<unknown>;
        const rightArray = rightValue[fieldName as F] as Array<unknown>;

        return {
          ...acc,
          [fieldName]: leftArray.filter((v, i) => !isEqual(v, rightArray[i])),
        };
      }

      if (isEqual(leftValue[fieldName as F], rightValue[fieldName as F])) {
        return acc;
      }

      return {
        ...acc,
        [fieldName]: leftValue[fieldName as F],
      };
    },
    {} as Record<F, T[F]>,
  );
};

export const getChangeEventValues = <
  T,
  R extends PropertyKey = never,
  F extends keyof (ModifiedEventLogType<ModifiedLogLegType<T>, R> | T) = never,
>(
  id: string,
  data: ModifiedEventLogType<ModifiedLogLegType<T>, R>,
  fields: F[] = [],
): Omit<ModifiedFieldValue<Record<F, T[F]>>, 'fieldName'> => {
  const oldValue = fields.reduce(
    (acc, field) => {
      const prevValue = unwrapModifiedEventLog(data[field] as ModifiedEventLog<T, R>, getPreviousEventValues);

      return {
        ...acc,
        [field]: prevValue,
      };
    },
    {} as Record<F, T[F]>,
  );

  const newValue = fields.reduce(
    (acc, field) => {
      const prevValue = unwrapModifiedEventLog(data[field] as ModifiedEventLog<T, R>);

      return {
        ...acc,
        [field]: prevValue,
      };
    },
    {} as Record<F, T[F]>,
  );

  const isValuesEqual = isEqual(oldValue, newValue);

  if (isValuesEqual) {
    return {
      id: `${id}-${fields.join('|')}`,
      operationType: EventOperationType.NoChange,
      oldValue: null,
      newValue: null,
    };
  }

  const hasOldValues = Object.values(oldValue).some(Boolean);
  const hasNewValues = Object.values(newValue).some(Boolean);

  const addRemoveOperationType = hasOldValues ? EventOperationType.Removed : EventOperationType.Added;
  const operationType = hasOldValues && hasNewValues ? EventOperationType.Modified : addRemoveOperationType;

  return {
    id: `${id}-${fields.join('|')}`,
    operationType,
    oldValue: hasOldValues ? oldValue : null,
    newValue: hasNewValues ? newValue : null,
  };
};
